import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass, NgFor } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DocumentService } from '../../services/documents.service';
import { UNDERSCORE, PERCENTAGE, EMPTYSPACE } from '../../../shared/utils/constant';
import { Document, DocumentLine, FieldOfDocument, OrderNumbers } from '../../models/document';
import { UserService } from '../../../shared/services/user.service';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { DbAnalyticsService } from '../../../shared/services/db-analytics.service';

@Component({
	selector: 'copo-invoice-card',
	templateUrl: './invoice-card.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatRippleModule,

		MatButtonModule,
		MatIconModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		NgClass,
		NgFor,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		SafeUrlPipe
	]
})
export class InvoiceCardComponent implements OnDestroy {
	invoiceFields: readonly FieldOfDocument[] = [
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'invoiceAmount',
		'invoiceAmountWithoutVat',
		'invoiceCurrency',
		'invoiceTaxAmount',
		'invoicedQuantity',
		'documentLines'
	];

	invoiceData?: Document;
	id: number;
	invoiceDisplayed = false;
	allBatchesDisplayed = true;
	invoiceSub: Subscription;
	isLoading = false;
	isOffline = false;
	isDocumentNotFound = false;
	orderNumbers: OrderNumbers[] = [];
	readonly UNDERSCORE = UNDERSCORE;
	readonly PERCENTAGE = PERCENTAGE;
	readonly EMPTYSPACE = EMPTYSPACE;
	constructor(
		private readonly documentService: DocumentService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		const { id: idParam } = <{ id?: string }> this.route.snapshot.params;
		this.id = Number(idParam);
		const { invoiceNumber, invoiceDate } = <{ invoiceNumber?: string; invoiceDate?: string }>
			this.route.snapshot.queryParams;
		this.isOffline = !navigator.onLine;
		const { user } = this.userService;
		this.invoiceDisplayed = user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.isLoading = true;
		const invoiceRequest = idParam
			? this.documentService.getDocumentDetails(this.id, this.invoiceFields)
			: invoiceNumber && invoiceDate
				? this.documentService.getInvoiceDetailsByNumberAndDate(invoiceNumber, invoiceDate, this.invoiceFields)
				: of(<Document>{});
		this.invoiceSub = invoiceRequest
			.subscribe({
				next: data => {
					if (Object.keys(data).length === 0) {
						this.isDocumentNotFound = true;
						this.isLoading = false;
					}
					this.invoiceData = data;
					for (const invoice of this.invoiceData.documentLines) {
						invoice.isSelected = true;
					}
					this.isLoading = false;
					this.getUniqueOrderName();
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	getUniqueOrderName() {
		const orderReference = new Set<string>();
		if (this.invoiceData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.invoiceData.documentLines) {
			if (docLine.customerOrderItemReference) {
				orderReference.add(`${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`);
			} else {
				orderReference.add(docLine.customerOrderReference);
			}
		}
		for (const orderRef of orderReference) {
			const orderNumber = {
				key: orderRef,
				isSelected: false
			};
			this.orderNumbers.push(orderNumber);
		}
	}

	getOrderName(docLine: DocumentLine) {
		if (docLine.customerOrderReference && docLine.customerOrderItemReference) {
			return `${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`;
		} else if (docLine.customerOrderReference && !docLine.customerOrderItemReference) {
			return docLine.customerOrderReference;
		}
		return UNDERSCORE;
	}

	displayAllBatches() {
		this.allBatchesDisplayed = true;
		if (this.invoiceData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.invoiceData.documentLines) {
			docLine.isSelected = true;
		}
	}

	filterBatch(orderNumber: OrderNumbers) {
		this.allBatchesDisplayed = false;
		if (this.invoiceData?.documentLines === undefined) {
			return;
		}
		for (const orderNum of this.orderNumbers) {
			orderNum.isSelected = orderNumber.key === orderNum.key;
		}
		for (const line of this.invoiceData.documentLines) {
			line.isSelected = orderNumber.key === (
				line.customerOrderItemReference
					? `${ line.customerOrderReference }/${ line.customerOrderItemReference }`
					: line.customerOrderReference
			);
		}
	}

	totalCalculation(documentLines: DocumentLine[], type: string) {
		const orderReference = new Set<string>();
		let sum = 0;
		switch (type) {
		case 'invoiceAmount':
			for (const line of documentLines) {
				const ref = !line.supplierOrderReferenceItem
					? `${ line.supplierOrderReference }/${ line.dispatchAdviceNumber }`
					: `${ line.supplierOrderReference }/${ line.supplierOrderReferenceItem }/${
						line.dispatchAdviceNumber }`;
				if (!orderReference.has(ref)) {
					orderReference.add(ref);
					sum += line.invoiceAmount;
				}
			}
			break;
		case 'invoiceAmountWithoutVat':
			for (const line of documentLines) {
				const ref = !line.supplierOrderReferenceItem
					? `${ line.supplierOrderReference }/${ line.dispatchAdviceNumber }`
					: `${ line.supplierOrderReference }/${ line.supplierOrderReferenceItem }/${
						line.dispatchAdviceNumber }`;
				if (!orderReference.has(ref)) {
					orderReference.add(ref);
					sum += line.invoiceAmountWithoutVat;
				}
			}
			break;

		case 'invoiceTaxAmount':
			for (const line of documentLines) {
				const ref = !line.supplierOrderReferenceItem
					? `${ line.supplierOrderReference }/${ line.dispatchAdviceNumber }`
					: `${ line.supplierOrderReference }/${ line.supplierOrderReferenceItem }/${
						line.dispatchAdviceNumber }`;
				if (!orderReference.has(ref)) {
					orderReference.add(ref);
					sum += line.invoiceTaxAmount;
				}
			}
			break;
		}
		return sum;
	}

	getDownloadDocumentUrl(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	trackDownload(id: number) {
		this.dbAnalyticsService.trackAction('DOWNLOAD_PDF', { documentType: 'INVOICE', documentId: String(id) });
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	ngOnDestroy() {
		this.invoiceSub.unsubscribe();
	}
}
